import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TextField,
    Box,
    TableContainer
} from "@mui/material";
import React, { useState } from "react";

export default function MPStainlessSteelTable({ rows, onSearchChange, searchValue: searchValueProp }) {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [searchValue, setSearchValue] = useState(searchValueProp ?? "");

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchValue(value);
        if (onSearchChange) onSearchChange(value);
    };

    const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Paper
            sx={{
                width: "100%",
                maxWidth: "100%",
                p: 2,
                borderRadius: 2,
                overflow: "hidden",
            }}
        >
            <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", borderRadius: 4 }}>
                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Procurar..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    sx={{
                        width: 250,
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "12px",
                            transition: "all 0.2s ease-in-out",
                            "&:hover fieldset": { borderColor: "var(--text-primary)" },
                            "&.Mui-focused fieldset": { borderColor: "var(--color-primary)" },
                        },
                        "& .MuiInputBase-input": { color: "var(--text-primary)" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "var(--text-primary)" },
                    }}
                />
            </Box>

            <TableContainer
                sx={{
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: "auto",
                    "&::-webkit-scrollbar": {
                        height: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "var(--bg-body)",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "var(--border-color)",
                        borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                        backgroundColor: "var(--text-muted)",
                    },
                }}
            >
                <Table
                    sx={{
                        borderCollapse: "collapse",
                    }}
                >
                    <TableHead>
                        <TableRow sx={{ "& th": { border: "1px solid var(--border-color)", textAlign: "center", fontWeight: "bold", fontSize: "0.75rem", px: 0.5, py: 1 } }}>
                            <TableCell colSpan={2}>Steel grade</TableCell>
                            <TableCell colSpan={1} rowSpan={2}>Product-form</TableCell>
                            <TableCell colSpan={1} rowSpan={2}>Thickness<br />t<br />mm<br />max.</TableCell>
                            <TableCell colSpan={1}>0.2 %<br />proof<br />strength</TableCell>
                            <TableCell colSpan={1}>1.0 %<br />proof<br />strength</TableCell>
                            <TableCell colSpan={1} rowSpan={2}>Tensile<br />strength</TableCell>
                            <TableCell colSpan={2}>Elongation after<br />fracture</TableCell>
                            <TableCell colSpan={3}>Impact energy<br />(ISO-V)</TableCell>
                            <TableCell colSpan={2}>Resistance to<br />intergranular<br />corrosion</TableCell>
                        </TableRow>

                        <TableRow sx={{ "& th": { border: "1px solid var(--border-color)", textAlign: "center", fontWeight: "bold", fontSize: "0.75rem", px: 0.5, py: 1 } }}>
                            <TableCell>Steel name</TableCell>
                            <TableCell>Steel number</TableCell>
                            <TableCell colSpan={1}>R<sub>p0.2</sub><br />MPa<br />min.<br />(tr.)</TableCell>
                            <TableCell colSpan={1}>R<sub>p1.0</sub><br />MPa<br />min.</TableCell>
                            <TableCell colSpan={1}>A<sub>80mm</sub><br />≥ 3 mm<br />thick<br />%<br />min.<br />(long+tr.)</TableCell>
                            <TableCell colSpan={1}>A°<br />≥ 3 mm<br />thick<br />%<br />min.<br />(long+tr.)</TableCell>
                            <TableCell colSpan={2}>KV2<br />J<br />min.<br />at 20°C</TableCell>
                            <TableCell colSpan={1}>at<br />196<br />°C<br />(tr.)</TableCell>
                            <TableCell colSpan={1}>in the<br />delivery<br />condition</TableCell>
                            <TableCell colSpan={1}>in the<br />sensi-<br />tized<br />condition</TableCell>
                        </TableRow>

                    </TableHead>

                    <TableBody>
                        {paginatedRows.map((row) =>
                            row.properties.map((property, propIndex) => (
                                <TableRow
                                    key={`${row.id}-${propIndex}`}
                                    sx={{
                                        "& td": {
                                            border: "1px solid var(--border-color)",
                                            textAlign: "center",
                                            whiteSpace: "nowrap",
                                            px: 1,
                                        },
                                    }}
                                >
                                    {propIndex === 0 && (
                                        <>
                                            <TableCell rowSpan={row.properties.length}>
                                                {row.steelName}
                                            </TableCell>
                                            <TableCell rowSpan={row.properties.length}>
                                                {row.steelNumber}
                                            </TableCell>
                                        </>
                                    )}

                                    <TableCell>{property.productForm}</TableCell>
                                    <TableCell>{property.thicknessTMax}</TableCell>
                                    <TableCell>{property.rp02MpaMin}</TableCell>
                                    <TableCell>{property.rp10MpaMin}</TableCell>
                                    <TableCell>{property.tensileStrengthRmMpa}</TableCell>
                                    <TableCell>{property.elongationA80mmMin}</TableCell>
                                    <TableCell>{property.elongationAMin}</TableCell>
                                    <TableCell>{property.impactEnergy20CLong}</TableCell>
                                    <TableCell>{property.impactEnergy20TR}</TableCell>
                                    <TableCell>{property.impactEnergyMinus196C}</TableCell>
                                    <TableCell>{property.corrosionDeliveryCondition}</TableCell>
                                    <TableCell>{property.corrosionSensitizedCondition}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
            />
        </Paper>
    );
}